import { useEffect, useRef, useState } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import { ChatCircleDots, X, PaperPlaneTilt, Camera, Trash, Robot, Sparkle, Wrench } from "@phosphor-icons/react";
import { useNavigate } from "react-router-dom";

// Floating help button + slide-over panel. Also renders inline when used on the /admin/ai-help page.
export function OfficeAIHelpFloating() {
  const [open, setOpen] = useState(false);
  return (
    <>
      {!open && (
        <button onClick={() => setOpen(true)} data-testid="office-ai-help-fab"
          className="fixed bottom-5 right-5 z-40 h-14 w-14 rounded-full bg-gradient-to-br from-fuchsia-600 to-violet-700 text-white shadow-2xl flex items-center justify-center hover:scale-110 transition-transform"
          title="Office AI Help">
          <ChatCircleDots weight="fill" size={26}/>
        </button>
      )}
      {open && (
        <div className="fixed inset-0 z-[100] bg-slate-900/60" onClick={() => setOpen(false)} data-testid="office-ai-help-slideover">
          <div className="fixed right-0 top-0 bottom-0 w-full max-w-lg bg-white shadow-2xl flex flex-col" onClick={e => e.stopPropagation()}>
            <div className="p-4 bg-gradient-to-r from-fuchsia-700 to-violet-700 text-white flex items-center justify-between">
              <div>
                <div className="text-[10px] font-black uppercase tracking-widest opacity-80 flex items-center gap-1">
                  <Robot weight="fill" size={11}/> Office AI Help
                </div>
                <div className="font-black text-lg">Ask anything · attach photos</div>
              </div>
              <button onClick={() => setOpen(false)} className="w-8 h-8 flex items-center justify-center hover:bg-white/10"><X size={20}/></button>
            </div>
            <OfficeAIHelpPanel embedded/>
          </div>
        </div>
      )}
    </>
  );
}

// Reusable inner panel — used inline in slide-over and in the full page.
export default function OfficeAIHelpPanel({ embedded = false }) {
  const [threads, setThreads] = useState([]);
  const [activeId, setActiveId] = useState(null);
  const [thread, setThread] = useState(null);
  const [text, setText] = useState("");
  const [photos, setPhotos] = useState([]);
  const [busy, setBusy] = useState(false);
  const [drafting, setDrafting] = useState(false);
  const inputRef = useRef(null);
  const scrollerRef = useRef(null);
  const nav = useNavigate();

  const loadThreads = async () => {
    try { const r = await api.get("/office-ai-help/threads"); setThreads(r.data.threads || []); }
    catch { /* silent */ }
  };
  useEffect(() => { loadThreads(); }, []);
  useEffect(() => {
    if (!activeId) { setThread(null); return; }
    api.get(`/office-ai-help/threads/${activeId}`).then(r => setThread(r.data)).catch(() => setThread(null));
  }, [activeId]);
  useEffect(() => {
    if (scrollerRef.current) scrollerRef.current.scrollTop = scrollerRef.current.scrollHeight;
  }, [thread?.message_count]);

  const send = async () => {
    if (!text.trim() && photos.length === 0) return;
    setBusy(true);
    try {
      const fd = new FormData();
      fd.append("text", text);
      photos.forEach(p => fd.append("files", p));
      if (!activeId) {
        const r = await api.post("/office-ai-help/threads", fd, { headers: { "Content-Type": "multipart/form-data" }});
        setActiveId(r.data.id);
        setThread(r.data);
        loadThreads();
      } else {
        const r = await api.post(`/office-ai-help/threads/${activeId}/messages`, fd, { headers: { "Content-Type": "multipart/form-data" }});
        setThread(prev => prev ? { ...prev, messages: [...(prev.messages || []), r.data.user_message, r.data.ai_message], message_count: (prev.message_count || 0) + 2 } : prev);
      }
      setText(""); setPhotos([]);
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Failed")); }
    finally { setBusy(false); }
  };

  const del = async (id) => {
    if (!window.confirm("Delete this thread?")) return;
    try {
      await api.delete(`/office-ai-help/threads/${id}`);
      if (id === activeId) { setActiveId(null); setThread(null); }
      loadThreads();
    } catch { toast.error("Delete failed"); }
  };

  const turnIntoWO = async () => {
    if (!activeId || !thread || (thread.message_count || 0) < 2) {
      toast.error("Have a full Q&A first, then turn it into a WO");
      return;
    }
    setDrafting(true);
    try {
      const r = await api.post(`/office-ai-help/threads/${activeId}/draft-wo`);
      sessionStorage.setItem("fr_new_job_prefill", JSON.stringify(r.data));
      toast.success("Draft ready — opening New Job form");
      nav("/admin/jobs?new=1");
    } catch (e) { toast.error(formatApiErrorDetail(e?.response?.data?.detail ?? "Draft failed")); }
    finally { setDrafting(false); }
  };

  return (
    <div className={`flex ${embedded ? "flex-1 min-h-0" : "h-[70vh] border-2 border-slate-900 shadow-lg"}`} data-testid="office-ai-help-panel">
      {/* Threads sidebar */}
      <div className="w-48 shrink-0 border-r-2 border-slate-200 bg-slate-50 flex flex-col">
        <button onClick={() => { setActiveId(null); setThread(null); }} data-testid="new-thread-btn"
          className="m-2 h-9 bg-fuchsia-700 hover:bg-fuchsia-800 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center justify-center gap-1">
          <Sparkle weight="fill" size={12}/> New Question
        </button>
        <div className="flex-1 overflow-y-auto text-[11px]" data-testid="threads-list">
          {threads.length === 0 && (
            <div className="p-3 text-slate-500 text-center">No threads yet.<br/>All questions are shared.</div>
          )}
          {threads.map(t => (
            <button key={t.id} onClick={() => setActiveId(t.id)} data-testid={`thread-item-${t.id}`}
              className={`w-full text-left px-3 py-2 border-b border-slate-200 group ${t.id === activeId ? "bg-fuchsia-100" : "hover:bg-slate-100"}`}>
              <div className="font-black truncate">{t.title || "Untitled"}</div>
              <div className="text-slate-500 text-[10px]">{t.created_by_name || "—"} · {t.message_count} msg</div>
            </button>
          ))}
        </div>
      </div>

      {/* Conversation */}
      <div className="flex-1 flex flex-col min-w-0">
        <div ref={scrollerRef} className="flex-1 overflow-y-auto p-3 space-y-3 bg-white" data-testid="thread-messages">
          {!thread && (
            <div className="text-center text-slate-500 text-sm mt-6" data-testid="empty-thread">
              <Robot size={36} className="mx-auto text-slate-300"/>
              <p className="mt-2 font-bold">Describe an issue below.</p>
              <p className="text-[11px] mt-1">Attach photos if you have them. All threads are shared with your office.</p>
            </div>
          )}
          {thread?.messages?.map((m, i) => (
            <div key={m.id || i} className={`p-2.5 max-w-[85%] ${m.role === "assistant" ? "bg-fuchsia-50 border-l-4 border-fuchsia-500" : "bg-slate-100 ml-auto text-right"}`}
                 data-testid={`msg-${m.role}-${i}`}>
              <div className="text-[9px] font-black uppercase tracking-widest text-slate-500">
                {m.role === "assistant" ? "AI" : (m.author_name || "you")}
              </div>
              {m.photos?.length > 0 && (
                <div className="flex gap-1 mt-1 flex-wrap">
                  {m.photos.map(p => (
                    <div key={p.id} className="w-14 h-14 border border-slate-300 bg-slate-200 overflow-hidden"/>
                  ))}
                </div>
              )}
              <div className="mt-1 text-[13px] whitespace-pre-wrap leading-snug text-slate-900">{m.text}</div>
            </div>
          ))}
          {busy && (
            <div className="text-[11px] text-fuchsia-700 font-black uppercase tracking-widest flex items-center gap-1" data-testid="ai-thinking">
              <Sparkle weight="fill" size={10} className="animate-pulse"/> Thinking…
            </div>
          )}
        </div>

        {/* Composer */}
        <div className="border-t-2 border-slate-200 p-2 bg-slate-50">
          {photos.length > 0 && (
            <div className="flex gap-1 mb-1 flex-wrap" data-testid="photo-strip">
              {photos.map((p, i) => (
                <div key={i} className="relative w-12 h-12 border border-slate-400 overflow-hidden">
                  <img src={URL.createObjectURL(p)} alt="" className="w-full h-full object-cover"/>
                  <button onClick={() => setPhotos(photos.filter((_, x) => x !== i))}
                    className="absolute top-0 right-0 w-4 h-4 bg-red-600 text-white text-[9px] font-black">×</button>
                </div>
              ))}
            </div>
          )}
          <div className="flex items-end gap-1">
            <input ref={inputRef} type="file" multiple accept="image/*" className="hidden" data-testid="photo-input"
              onChange={e => { setPhotos([...photos, ...Array.from(e.target.files || [])].slice(0, 4)); if (inputRef.current) inputRef.current.value = ""; }}/>
            <button onClick={() => inputRef.current?.click()} data-testid="attach-photo-btn"
              className="h-10 w-10 border-2 border-slate-400 bg-white hover:bg-slate-100 flex items-center justify-center">
              <Camera size={16}/>
            </button>
            <textarea value={text} onChange={e => setText(e.target.value)}
              placeholder={activeId ? "Reply…" : "Describe an issue, or ask a question…"}
              data-testid="composer-input" rows={2}
              onKeyDown={e => { if (e.key === "Enter" && (e.metaKey || e.ctrlKey)) { e.preventDefault(); send(); } }}
              className="flex-1 h-10 min-h-[40px] max-h-[120px] px-2 py-1 text-sm border-2 border-slate-400 outline-none focus:border-slate-900 resize-none"/>
            <button onClick={send} disabled={busy || (!text.trim() && photos.length === 0)}
              data-testid="send-btn"
              className="h-10 px-3 bg-fuchsia-700 hover:bg-fuchsia-800 text-white font-black uppercase text-[10px] tracking-widest disabled:opacity-50 inline-flex items-center gap-1">
              <PaperPlaneTilt weight="fill" size={12}/> {busy ? "…" : "Ask"}
            </button>
          </div>
          <div className="text-[9px] text-slate-500 mt-1">
            All threads are shared with your office team. ⌘/Ctrl+Enter to send.
          </div>
          {activeId && (
            <div className="mt-1 flex items-center justify-between gap-2">
              <button onClick={() => del(activeId)} data-testid="delete-thread-btn"
                className="text-[10px] font-black uppercase tracking-widest text-red-600 hover:underline inline-flex items-center gap-1">
                <Trash size={10}/> Delete this thread
              </button>
              <button onClick={turnIntoWO} disabled={drafting || (thread?.message_count || 0) < 2}
                data-testid="turn-into-wo-btn"
                className="h-8 px-2.5 bg-emerald-600 hover:bg-emerald-700 disabled:opacity-40 text-white font-black uppercase text-[10px] tracking-widest inline-flex items-center gap-1">
                <Wrench weight="fill" size={11}/> {drafting ? "Drafting…" : "Turn into WO"}
              </button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
